/**
 * Browser-side auth headers for `/api/platform/*`.
 *
 * Radar has two identities: a `pm_session` cookie (sent automatically) and a
 * `pm_platform_token` in localStorage (which is NOT, and must be attached by
 * hand as `x-platform-token`). Every authenticated platform route resolves
 * either via `lib/platform/auth.ts:resolveAuth`.
 *
 * This lived as a private function inside the dashboard page, so any call site
 * outside that file — a component, a hook — had no way to reach it and
 * silently shipped an unauthenticated request. For a token-only paid user
 * (no session cookie) that is a 401, and a 401 that a caller reads as an
 * empty list looks exactly like "you have no data".
 *
 * That is what happened to the trend sparkline: BOTH the POST that writes a
 * snapshot and the GET that reads it omitted the header, so a token-only
 * user's history was never written and never read, while the audit-runs
 * history right next to it worked because those calls used the helper.
 *
 * Prefer `platformFetch` over `platformHeaders`: it cannot be called without
 * the header, which is the point.
 */

const TOKEN_STORAGE_KEY = 'pm_platform_token'

export function platformHeaders(
  extra: Record<string, string | null | undefined> = {}
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (typeof window !== 'undefined') {
    const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY)
    if (storedToken) headers['x-platform-token'] = storedToken
  }
  // Nullish values are dropped so we never stamp an empty header.
  for (const [k, v] of Object.entries(extra)) {
    if (v) headers[k] = v
  }
  return headers
}

/**
 * `fetch` with the platform identity attached. Use for every
 * `/api/platform/*` request that requires auth.
 */
export function platformFetch(
  input: string,
  init: RequestInit & {
    extraHeaders?: Record<string, string | null | undefined>
  } = {}
): Promise<Response> {
  const { extraHeaders, ...rest } = init
  return fetch(input, {
    ...rest,
    headers: {
      ...platformHeaders(extraHeaders),
      ...(rest.headers as Record<string, string> | undefined),
    },
  })
}
